export type TreatmentFormData = {
  treatmentkey: number;
  treatment: string;
  treatments: string;
  treatmentId: string;
  type: string;
  types: string;
  typeId: string;
  subtype: string;
  subtypes: string;
  subtypeId: string;
  toothNos: string;
  toothNo: string;
  prices: number;
  price: number;
  description: string;
  createdAt: Date;
  quantity: number;
  note: string;
  surface: string;
  status: string;
  didBy?: string;
};

export const getCatalogLongTermEnabled = (
  catalog: any[],
  treatmentId: string,
  typeId?: string,
  subtypeId?: string
) => {
  if (!treatmentId || !catalog?.length) return false;

  const main = catalog.find(
    (item: any) => String(item._id) === String(treatmentId)
  );
  if (!main) return false;

  if (subtypeId && typeId) {
    const type = (main.type || []).find(
      (t: any) => String(t._id) === String(typeId)
    );
    const subtype = (type?.subType || []).find(
      (s: any) => String(s._id) === String(subtypeId)
    );
    if (subtype?.longTermTreatment) return true;
  }

  if (typeId) {
    const type = (main.type || []).find(
      (t: any) => String(t._id) === String(typeId)
    );
    if (type?.longTermTreatment) return true;
  }

  return !!main.longTermTreatment;
};

export const buildTreatmentPayload = (
  formData: TreatmentFormData,
  resolvedStatus: string
) => {
  const quantity = Number(formData.quantity) || 1;
  const unitPrice = Number(formData.prices) || 0;
  const price = String(unitPrice * quantity);
  const isOthers =
    (formData.treatment || "").trim().toLowerCase() === "others";

  const payload: Record<string, unknown> = {
    treatment: isOthers ? formData.description : formData.treatment,
    treatmentId: formData.treatmentId
      ? String(formData.treatmentId)
      : undefined,
    type: formData.type || undefined,
    typeId: formData.typeId ? String(formData.typeId) : undefined,
    subtype: formData.subtype || undefined,
    subtypeId: formData.subtypeId ? String(formData.subtypeId) : undefined,
    price,
    quantity,
    note: formData.note ?? "",
    status: resolvedStatus,
    description: formData.description ?? "",
  };

  if (formData.toothNos) {
    payload.toothNos = formData.toothNos;
    const firstTooth = formData.toothNos.split(",")[0]?.trim();
    if (firstTooth && /^\d+$/.test(firstTooth)) {
      payload.toothNo = Number(firstTooth);
    }
  } else if (formData.toothNo) {
    const tooth = String(formData.toothNo).trim();
    if (/^\d+$/.test(tooth)) {
      payload.toothNo = Number(tooth);
    }
  }

  return payload;
};

export const hasTreatmentSelection = (
  formData: TreatmentFormData,
  isEdit: boolean
) => {
  if (isEdit) {
    return Boolean(
      formData.treatmentId || formData.treatment || formData.treatments
    );
  }
  return formData.treatments !== "";
};

export const hasToothSelection = (formData: {
  toothNos?: string;
  toothNo?: string | number;
}) => {
  const fromList = String(formData.toothNos ?? "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  if (fromList.length > 0) return true;
  return String(formData.toothNo ?? "").trim() !== "";
};

export type TreatmentFormValidationResult =
  | { valid: true; payload: Record<string, unknown> }
  | { valid: false; message: string };

export const validateTreatmentForm = (
  formData: TreatmentFormData,
  catalog: any[],
  isEdit: boolean,
  defaultStatus: string
): TreatmentFormValidationResult => {
  if (!hasTreatmentSelection(formData, isEdit)) {
    return { valid: false, message: "Treatment is required" };
  }

  if (!hasToothSelection(formData)) {
    return { valid: false, message: "Select at least one tooth" };
  }

  const isOthers =
    (formData.treatment || "").trim().toLowerCase() === "others";
  if (isOthers && !formData.description) {
    return { valid: false, message: "Description is required for Others" };
  }

  const isCatalogLongTerm = getCatalogLongTermEnabled(
    catalog,
    formData.treatmentId,
    formData.typeId,
    formData.subtypeId
  );
  const resolvedStatus = isCatalogLongTerm ? "long term" : formData.status || defaultStatus;

  return {
    valid: true,
    payload: buildTreatmentPayload(formData, resolvedStatus),
  };
};
